<template>
 <div class="history-modal">
  <div class="history-header">
   <div class="header-icon"><i class="fas fa-history"></i></div>
   <div class="header-title">
    <h2>{{ title }}</h2>
    <span v-if="subtitle" class="subtitle">{{ subtitle }}</span>
   </div>
   <button type="button" class="close-btn" title="Close" @click="$emit('close')"><i class="fas fa-times"></i></button>
  </div>

  <div class="history-body">
   <div class="table-wrap">
    <table>
     <thead>
      <tr>
       <th v-for="column in columns" :key="column.key" :class="column.key">{{ column.label }}</th>
      </tr>
     </thead>
     <tbody>
      <tr v-if="loading">
       <td :colspan="columns.length" class="empty-row">
        <v-progress-circular indeterminate size="26" width="3" color="#1976d2"></v-progress-circular>
       </td>
      </tr>
      <tr v-else-if="!sortedRecords.length">
       <td :colspan="columns.length" class="empty-row">No activity recorded yet</td>
      </tr>
      <tr v-for="(record, index) in sortedRecords" :key="index">
       <td>{{ record.employee || "-" }}</td>
       <td class="reason">{{ record.reason || "-" }}</td>
       <td class="nowrap">{{ formatDate(record.date) }}</td>
       <td>
        <span class="status-pill" :class="`status-pill--${statusOf(record.action).tone}`">
         <i class="fas" :class="statusOf(record.action).icon"></i> {{ statusOf(record.action).text }}
        </span>
       </td>
      </tr>
     </tbody>
    </table>
   </div>
  </div>

  <div class="history-footer">
   <span>{{ records.length }} activity {{ records.length === 1 ? "record" : "records" }}</span>
   <v-btn depressed class="footer-close" @click="$emit('close')">Close</v-btn>
  </div>
 </div>
</template>

<script>
import moment from "moment";

// action codes stored in the driver/vehicle reason history
const STATUS = {
 0: { text: "Activated", tone: "green", icon: "fa-check-circle" },
 1: { text: "Deactivated", tone: "red", icon: "fa-ban" },
 2: { text: "Rejected", tone: "amber", icon: "fa-times-circle" },
 5: { text: "Deleted", tone: "red", icon: "fa-trash-alt" },
};
const VERIFIED = { text: "Verified", tone: "green", icon: "fa-check-circle" };

export default {
 name: "ActivityHistory",
 props: {
  title: { type: String, required: true },
  subtitle: { type: String, default: "" },
  // [{ employee, reason, date (ms), action }]
  records: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
 },
 data() {
  return {
   columns: [
    { key: "employee", label: "Employee" },
    { key: "reason", label: "Reason" },
    { key: "date", label: "Date & Time" },
    { key: "status", label: "Status" },
   ],
  };
 },
 computed: {
  // newest first
  sortedRecords() {
   const time = (record) => (record.date ? Number(moment(record.date).valueOf()) || 0 : 0);
   return [...this.records].sort((a, b) => time(b) - time(a));
  },
 },
 methods: {
  statusOf(action) {
   return STATUS[action] || VERIFIED;
  },
  formatDate(date) {
   if (!date) return "Not found";
   const formatted = moment(date).format("YYYY-MM-DD HH:mm:ss");
   return formatted === "Invalid date" ? "Not found" : formatted;
  },
 },
};
</script>

<style lang="scss" scoped>
$border: #e5e9f0;
$text: #1f2937;
$muted: #6b7280;

.history-modal {
 display: flex;
 flex-direction: column;
 max-height: 90vh;
 background: #fff;
}

.history-header {
 display: flex;
 align-items: center;
 gap: 16px;
 padding: 20px 28px 16px;

 .header-icon {
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 24px;
  color: #1976d2;
  background: #e8f0fe;
 }

 .header-title {
  flex: 1;
  min-width: 0;

  h2 {
   margin: 0;
   font-size: 24px;
   font-weight: 700;
   color: $text;
  }

  .subtitle {
   font-size: 16px;
   color: $muted;
  }
 }

 .close-btn {
  align-self: flex-start;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #374151;
  border-radius: 6px;

  &:hover {
   background: #f3f4f6;
  }
 }
}

.history-body {
 flex: 1;
 min-height: 0;
 padding: 0 28px;
 display: flex;
}

.table-wrap {
 flex: 1;
 overflow-y: auto;
 max-height: 62vh;
 border: 1px solid $border;
 border-radius: 8px;

 table {
  width: 100%;
  border-collapse: collapse;
 }

 th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 14px 18px;
  text-align: left;
  font-size: 15px;
  font-weight: 600;
  color: $text;
  background: #f1f4f8;
  white-space: nowrap;

  &.reason {
   width: 40%;
  }
 }

 td {
  padding: 10px 18px;
  font-size: 15px;
  color: #374151;
  border-top: 1px solid #f0f2f5;
 }

 tbody tr:nth-child(even) {
  background: #f8fafc;
 }

 tbody tr:hover {
  background: #f1f5fb;
 }

 .nowrap {
  white-space: nowrap;
 }

 .empty-row {
  padding: 36px;
  text-align: center;
  color: $muted;
 }
}

.status-pill {
 display: inline-flex;
 align-items: center;
 gap: 8px;
 padding: 4px 14px;
 font-size: 13px;
 font-weight: 600;
 border-radius: 20px;
 white-space: nowrap;
}
.status-pill--green {
 color: #1e7b34;
 background: #e6f4ea;
}
.status-pill--red {
 color: #c62828;
 background: #fde8e8;
}
.status-pill--amber {
 color: #b45309;
 background: #fff4e0;
}

.history-footer {
 display: flex;
 align-items: center;
 justify-content: space-between;
 margin-top: 18px;
 padding: 14px 28px;
 border-top: 1px solid $border;
 font-size: 14px;
 color: $muted;

 .footer-close {
  height: 42px;
  padding: 0 26px;
  font-size: 16px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  color: $text;
  background: #f3f4f6 !important;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
 }
}
</style>
